import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Button, Input, Avatar, message, Modal } from "antd";
import { SendOutlined, CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import { device, useBreakpoints } from "../../lib/breakpoints";
import { getDebugMode } from "../../helpers/constants";

/** Render Leo's lightweight markdown (**bold**) without exposing raw markers. */
const renderLeoMarkdown = (text) => {
  if (text == null) return null;
  const value = String(text);
  if (!value) return null;

  return value.split("\n").map((line, lineIndex, lines) => (
    <React.Fragment key={`${lineIndex}-${line.slice(0, 24)}`}>
      {line.split(/(\*\*[^*]+\*\*)/g).map((part, partIndex) =>
        part.startsWith("**") && part.endsWith("**") && part.length > 4 ? (
          <strong key={partIndex}>{part.slice(2, -2)}</strong>
        ) : (
          <React.Fragment key={partIndex}>{part}</React.Fragment>
        )
      )}
      {lineIndex < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ));
};

/**
 * LeoChatWidget - Reusable Leo chat widget component
 *
 * This is the base component that provides the UI and structure for Leo chat widgets.
 * It can be customized via props for different use cases (landlord dashboard, public listings, etc.)
 *
 * @param {Object} props
 * @param {boolean} props.isVisible - Whether the widget should be visible
 * @param {Function} props.fetchInitialMessage - Function to fetch the initial message
 * @param {Function} props.handleSendMessage - Function to handle sending a message
 * @param {Function} props.getChatStorageKey - Function to get localStorage key for chat persistence
 * @param {Function} props.getChatTimestampKey - Function to get localStorage key for timestamp
 * @param {string} props.tooltipText - Text to show in the welcome tooltip
 * @param {string} props.iconPath - Path to icon image for messages/top bar (defaults to leo.png)
 * @param {string} props.buttonIconPath - Path to icon image for floating button (defaults to leo-chat.png)
 * @param {string} props.title - Chat title (defaults to "Leo AI")
 * @param {string} props.subtitle - Chat subtitle
 * @param {string} props.placeholder - Input placeholder text
 * @param {boolean} props.showWelcomeTooltip - Whether to show the welcome tooltip
 * @param {Function} props.onTooltipDismiss - Callback when tooltip is dismissed
 * @param {boolean} props.requireAuth - Whether authentication is required (affects error messages)
 * @param {Function} props.onWidgetToggle - Optional callback when widget is toggled
 * @param {boolean} props.showFullDisclaimer - Whether to show full disclaimer with terms/privacy links (for public listings)
 * @param {Function} props.onReset - Optional callback when chat is reset (for clearing additional storage)
 */
const LeoChatWidget = ({
  isVisible = true,
  fetchInitialMessage,
  handleSendMessage: handleSendMessageProp,
  getChatStorageKey,
  getChatTimestampKey,
  // External trigger: bump this number to force-open the widget.
  // (Designed for "Apply now" / "Schedule tour" CTAs on the page.)
  openSignal,
  // Optional: after openSignal opens the panel, set the composer to this text (e.g. lease renewal deep link).
  pendingComposerText,
  // Optional: render non-text messages (cards) inside the chat stream.
  // Must be pure (based on message object), since messages are persisted to localStorage.
  renderCustomMessage,
  // Optional: handle actions emitted by custom message UI (e.g., clicking a time slot).
  // Signature: async (action, helpers) => void
  onCustomAction,
  tooltipText,
  iconPath = "/icons/leo/leo.png",
  buttonIconPath = "/icons/leo/leo-chat.png",
  title = "Leo AI",
  subtitle = "Your AI assistant",
  placeholder = "Ask Leo AI...",
  showWelcomeTooltip = false,
  onTooltipDismiss,
  requireAuth = false,
  onWidgetToggle,
  showFullDisclaimer = false,
  onReset,
  // Compliance: consent modal trigger when backend returns consent_required
  onConsentRequired,
  // Compliance: show "AI-generated response. Final decisions subject to human review."
  showAiDisclaimerFooter = false,
  // Show "Please do not share sensitive medical or health information." in disclaimer block (e.g. listing chat)
  showMedicalDisclaimer = false,
  // On desktop, expand to full screen with ChatGPT-style layout (e.g. listing agent chat)
  fullScreenOnDesktop = false,
  // Mobile widget context (optional, for landlord widget)
  isFeedbackWidgetActive = false,
  setActiveWidget,
  clearActiveWidget,
}) => {
  const { isMobile, isDesktop, isTablet } = useBreakpoints();
  const [isOpen, setIsOpen] = useState(false);
  const useFullScreenLayout =
    fullScreenOnDesktop && (isDesktop || isTablet) && isOpen;
  const [isFullyOpen, setIsFullyOpen] = useState(false);
  const [isLoadingFading, setIsLoadingFading] = useState(false);
  const [isChatReady, setIsChatReady] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingInitialMessage, setIsLoadingInitialMessage] = useState(false);
  const [initialMessageError, setInitialMessageError] = useState(false);
  const [hasLoadedInitialMessage, setHasLoadedInitialMessage] = useState(false);
  const [isResetDisabled, setIsResetDisabled] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [conversationState, setConversationState] = useState(null);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [viewLogicModal, setViewLogicModal] = useState({
    open: false,
    text: "",
  });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const tooltipTimeoutRef = useRef(null);
  const tooltipShowTimeoutRef = useRef(null);
  const lastOpenSignalRef = useRef(openSignal);

  const scrollToBottom = React.useCallback((behavior = "auto") => {
    if (messagesEndRef.current) {
      // Use 'auto' to snap immediately (no slow scroll) so the newest message is visible.
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  // Chat persistence helpers
  const saveChatToStorage = React.useCallback(
    (chatMessages) => {
      try {
        const storageKey = getChatStorageKey();
        const timestampKey = getChatTimestampKey();
        localStorage.setItem(storageKey, JSON.stringify(chatMessages));
        localStorage.setItem(timestampKey, Date.now().toString());
      } catch (error) {
        console.warn("Failed to save chat to localStorage:", error);
      }
    },
    [getChatStorageKey, getChatTimestampKey]
  );

  const loadChatFromStorage = React.useCallback(() => {
    try {
      const storageKey = getChatStorageKey();
      const timestampKey = getChatTimestampKey();
      const storedChat = localStorage.getItem(storageKey);
      const storedTimestamp = localStorage.getItem(timestampKey);

      if (!storedChat || !storedTimestamp) {
        return null;
      }

      const timestamp = parseInt(storedTimestamp, 10);
      const now = Date.now();
      const twentyFourHoursInMs = 24 * 60 * 60 * 1000;

      if (now - timestamp > twentyFourHoursInMs) {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(timestampKey);
        return null;
      }

      const parsedMessages = JSON.parse(storedChat);
      return parsedMessages.map((msg) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
    } catch (error) {
      console.warn("Failed to load chat from localStorage:", error);
      return null;
    }
  }, [getChatStorageKey, getChatTimestampKey]);

  // Load chat from storage on mount
  useEffect(() => {
    const savedMessages = loadChatFromStorage();
    if (savedMessages && savedMessages.length > 0) {
      setMessages(savedMessages);
      setHasLoadedInitialMessage(true);
      setIsChatReady(true);
    } else {
      setIsLoadingInitialMessage(true);
      fetchInitialMessage()
        .then((initialMessage) => {
          if (initialMessage) {
            setMessages([initialMessage]);
            saveChatToStorage([initialMessage]);
          }
          setHasLoadedInitialMessage(true);
          setInitialMessageError(false);
          setIsLoadingInitialMessage(false);
        })
        .catch((error) => {
          console.error("Error fetching initial message:", error);
          setInitialMessageError(true);
          setHasLoadedInitialMessage(true);
          setIsLoadingInitialMessage(false);
        });
    }
  }, [loadChatFromStorage, fetchInitialMessage, saveChatToStorage]);

  // Save messages to storage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      saveChatToStorage(messages);
    }
  }, [messages, saveChatToStorage]);

  useEffect(() => {
    if (hasLoadedInitialMessage && !isLoadingInitialMessage) {
      setIsChatReady(true);
    }
  }, [hasLoadedInitialMessage, isLoadingInitialMessage]);

  // Scroll to bottom when messages change or typing
  useEffect(() => {
    scrollToBottom("auto");
  }, [messages, isTyping, scrollToBottom]);

  // Scroll to bottom when widget opens
  useEffect(() => {
    if (isFullyOpen && messages.length > 0) {
      // Ensure the widget is rendered, then snap to bottom immediately.
      const scrollTimeout = setTimeout(() => {
        scrollToBottom("auto");
      }, 0);

      return () => clearTimeout(scrollTimeout);
    }
  }, [isFullyOpen, messages.length, scrollToBottom]);

  const handleResetChat = () => {
    const storageKey = getChatStorageKey();
    const timestampKey = getChatTimestampKey();
    localStorage.removeItem(storageKey);
    localStorage.removeItem(timestampKey);

    // Call optional onReset callback to clear additional storage (e.g., session IDs)
    if (onReset) {
      onReset();
    }

    setConversationState(null);

    setMessages([]);
    setHasLoadedInitialMessage(false);
    setInitialMessageError(false);
    setIsLoadingInitialMessage(true);
    setIsChatReady(false);
    setIsTyping(false);

    fetchInitialMessage()
      .then((initialMessage) => {
        if (initialMessage) {
          setMessages([initialMessage]);
          saveChatToStorage([initialMessage]);
          setIsChatReady(true);
        }
        setHasLoadedInitialMessage(true);
        setIsLoadingInitialMessage(false);
      })
      .catch((error) => {
        console.error("Error fetching initial message:", error);
        setInitialMessageError(true);
        setHasLoadedInitialMessage(true);
        setIsLoadingInitialMessage(false);
      });

    message.success("Chat reset successfully");
  };

  const openResetConfirm = () => {
    if (isResetDisabled) return;
    setIsResetConfirmOpen(true);
  };

  const handleSendMessage = async () => {
    if (!inputValue || inputValue.trim().length === 0) {
      message.error("Please enter a message before sending.");
      return;
    }

    if (requireAuth) {
      message.error("Please log in to use the AI assistant");
      return;
    }

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: inputValue,
      timestamp: new Date(),
    };

    // Add user message to UI immediately for better UX
    setMessages((prev) => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue("");
    setIsTyping(true);

    try {
      // Call the provided handler with messages BEFORE user message is added
      // The handler will add the user message to the chat history itself
      const response = await handleSendMessageProp(currentInput, messages);

      // Compliance: backend may return consent_required / consentRequired: show modal, remove optimistic user message, do not add AI message
      const consentRequired =
        response &&
        typeof response === "object" &&
        (response.consentRequired || response.consent_required === true);
      if (consentRequired) {
        setMessages((prev) => {
          const next = prev.slice(0, -1);
          saveChatToStorage(next);
          return next;
        });
        onConsentRequired?.();
        setIsTyping(false);
        return;
      }

      // Extract response text - handle different response formats
      let responseText = "";
      let requiresAction = null;
      let additionalMessages = null;
      let decisionExplanation = null;
      if (typeof response === "string") {
        responseText = response;
      } else if (response && typeof response === "object") {
        responseText =
          response.text ||
          response.message ||
          response.response ||
          "Sorry, I encountered an error. Please try again.";
        requiresAction = response.requiresAction || null;
        additionalMessages = Array.isArray(response.additionalMessages)
          ? response.additionalMessages
          : null;
        decisionExplanation = response.decisionExplanation ?? null;

        // Store conversation state if provided
        if (response.conversationState) {
          setConversationState(response.conversationState);
        }
      } else {
        responseText = "Sorry, I encountered an error. Please try again.";
      }

      if (!responseText || responseText.trim().length === 0) {
        console.warn("[LeoChatWidget] Empty response received from API");
        responseText = "Sorry, I received an empty response. Please try again.";
      }

      const aiResponse = {
        id: Date.now() + 1,
        type: "ai",
        content: responseText,
        timestamp: new Date(),
        requiresAction,
        decisionExplanation: decisionExplanation || undefined,
      };

      // Use functional update to ensure we're adding to the correct state
      // The user message should already be in state from the earlier setMessages call
      setMessages((prev) => {
        // Double-check that user message is in the state
        const userMsgExists = prev.some(
          (msg) =>
            msg.id === userMessage.id && msg.content === userMessage.content
        );
        if (!userMsgExists) {
          console.warn(
            "[LeoChatWidget] User message not found in state, adding it now"
          );
          const messagesWithUser = [...prev, userMessage];
          const newMessages = [...messagesWithUser, aiResponse];
          saveChatToStorage(newMessages);
          return newMessages;
        }
        let newMessages = [...prev, aiResponse];
        if (additionalMessages && additionalMessages.length > 0) {
          // Normalize timestamps if caller passed strings
          const normalized = additionalMessages.map((m) => ({
            ...m,
            timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
          }));
          newMessages = [...newMessages, ...normalized];
        }
        saveChatToStorage(newMessages);
        return newMessages;
      });
    } catch (error) {
      console.error("Error sending message:", error);
      let errorMessage = "Sorry, I ran into an issue. Please try again.";
      if (
        error.message.includes("Failed to fetch") ||
        error.message.includes("NetworkError")
      ) {
        errorMessage =
          "Unable to connect. Please check your internet connection and try again.";
      } else if (
        error.message &&
        // Only show the error message if it looks like human-readable text,
        // not a raw API / JSON / HTTP error string
        !error.message.includes("API call failed") &&
        !error.message.includes("{") &&
        !error.message.includes("500") &&
        !error.message.includes("502") &&
        !error.message.includes("503") &&
        !error.message.includes("overloaded") &&
        error.message.length < 200
      ) {
        errorMessage = error.message;
      }

      const errorResponse = {
        id: Date.now() + 1,
        type: "ai",
        content: errorMessage,
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const newMessages = [...prev, errorResponse];
        saveChatToStorage(newMessages);
        return newMessages;
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleWidget = React.useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      setIsFullyOpen(false);
      setIsLoadingFading(false);
      setIsResetConfirmOpen(false);

      if (setActiveWidget) {
        setActiveWidget("leo");
      }

      if (!hasLoadedInitialMessage) {
        setIsLoadingInitialMessage(true);
        fetchInitialMessage()
          .then((initialMessage) => {
            if (initialMessage) {
              setMessages([initialMessage]);
              saveChatToStorage([initialMessage]);
            }
            setHasLoadedInitialMessage(true);
          })
          .catch((error) => {
            console.error("Error fetching initial message:", error);
            setInitialMessageError(true);
            setHasLoadedInitialMessage(true);
          });
      } else {
        setIsChatReady(true);
      }

      // Wait briefly for the scale animation, then start fade-out faster
      setTimeout(() => {
        setIsLoadingFading(true);
        // Show content after fade-out animation completes
        setTimeout(() => {
          setIsFullyOpen(true);
          // Focus on input after content is visible
          setTimeout(() => {
            inputRef.current?.focus();
          }, 100);
        }, 200);
      }, 180);

      if (onWidgetToggle) {
        onWidgetToggle(true);
      }
    } else {
      setIsOpen(false);
      setIsFullyOpen(false);
      setIsLoadingFading(false);
      setIsResetConfirmOpen(false);

      if (clearActiveWidget) {
        clearActiveWidget();
      }

      if (onWidgetToggle) {
        onWidgetToggle(false);
      }
    }
  }, [
    isOpen,
    setActiveWidget,
    hasLoadedInitialMessage,
    fetchInitialMessage,
    saveChatToStorage,
    onWidgetToggle,
    clearActiveWidget,
  ]);

  // External open trigger (safe: won't toggle closed)
  useEffect(() => {
    if (openSignal === undefined || openSignal === null) return;
    if (lastOpenSignalRef.current === openSignal) return;
    lastOpenSignalRef.current = openSignal;

    let draft =
      typeof pendingComposerText === "string" && pendingComposerText.trim()
        ? pendingComposerText.trim()
        : "";
    if (!draft) {
      try {
        const raw = sessionStorage.getItem("leo_pending_composer");
        if (raw) {
          const o = JSON.parse(raw);
          if (
            o.text &&
            typeof o.text === "string" &&
            Date.now() - (o.at || 0) < 15 * 60 * 1000
          ) {
            draft = o.text.trim();
          }
          sessionStorage.removeItem("leo_pending_composer");
        }
      } catch {
        try {
          sessionStorage.removeItem("leo_pending_composer");
        } catch {
          /* ignore */
        }
      }
    }

    if (!isOpen) {
      toggleWidget();
    }
    if (draft) {
      setTimeout(() => setInputValue(draft), 450);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when openSignal bumps; avoid re-opening on pendingComposerText churn
  }, [openSignal, isOpen, toggleWidget]);

  // Check for debug mode - if debug flag is in URL, bypass localStorage but allow normal behavior
  const isDebugMode = getDebugMode();

  // Handle tooltip timing: show after 1 second, hide after 8 seconds
  useEffect(() => {
    // Clear any existing timeouts
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    if (tooltipShowTimeoutRef.current) {
      clearTimeout(tooltipShowTimeoutRef.current);
    }

    // Only show tooltip if showWelcomeTooltip prop is true and widget is not open
    if (showWelcomeTooltip && !isOpen && tooltipText) {
      // Show tooltip after 1 second delay (or immediately in debug mode)
      const showDelay = isDebugMode ? 0 : 1000;
      tooltipShowTimeoutRef.current = setTimeout(() => {
        setIsTooltipVisible(true);

        // Hide tooltip after 8 seconds (normal behavior, even in debug mode)
        tooltipTimeoutRef.current = setTimeout(() => {
          setIsTooltipVisible(false);
          // Call dismiss callback to mark as dismissed
          if (onTooltipDismiss) {
            onTooltipDismiss();
          }
        }, 8000);
      }, showDelay);
    } else {
      // Hide tooltip immediately if conditions aren't met
      setIsTooltipVisible(false);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      if (tooltipShowTimeoutRef.current) {
        clearTimeout(tooltipShowTimeoutRef.current);
      }
    };
  }, [showWelcomeTooltip, isOpen, tooltipText, onTooltipDismiss, isDebugMode]);

  const handleTooltipDismiss = () => {
    // Clear timeouts
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    if (tooltipShowTimeoutRef.current) {
      clearTimeout(tooltipShowTimeoutRef.current);
    }

    // Hide tooltip immediately
    setIsTooltipVisible(false);

    // Call dismiss callback
    if (onTooltipDismiss) {
      onTooltipDismiss();
    }
  };

  if (!isVisible) return null;

  const appendMessages = (newMsgs) => {
    if (!Array.isArray(newMsgs) || newMsgs.length === 0) return;
    setMessages((prev) => {
      const normalized = newMsgs.map((m) => ({
        ...m,
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
      }));
      const next = [...prev, ...normalized];
      saveChatToStorage(next);
      return next;
    });
  };

  const handleCustomAction = async (action) => {
    if (!onCustomAction) return;
    try {
      await onCustomAction(action, {
        conversationState,
        setConversationState,
        appendMessages,
        setIsTyping,
      });
    } catch (e) {
      console.error("[LeoChatWidget] Custom action failed:", e);
      message.error(e?.message || "Something went wrong. Please try again.");
      setIsTyping(false);
    }
  };

  return (
    <WidgetContainer>
      {/* Welcome Tooltip */}
      {isTooltipVisible && !isOpen && tooltipText && (
        <WelcomeMessage onClick={handleTooltipDismiss}>
          <div
            style={{
              display: "block",
              margin: 0,
              padding: 0,
              textAlign: "left",
            }}
          >
            <div
              style={{
                fontSize: "0.95rem",
                lineHeight: "1.5",
                color: "var(--color-text)",
                fontFamily: "var(--font-secondary)",
                fontWeight: "400",
                wordWrap: "break-word",
                wordBreak: "break-word",
                hyphens: "auto",
                whiteSpace: "pre-line",
                textAlign: "left",
                letterSpacing: "-0.01em",
              }}
            >
              {tooltipText}
            </div>
          </div>
          <Arrow />
        </WelcomeMessage>
      )}

      {/* Floating Button - hidden on mobile when any widget is open */}
      {(isMobile ? !isOpen && !isFeedbackWidgetActive : true) && (
        <FloatingButton
          onClick={toggleWidget}
          $isOpen={isOpen}
          type="primary"
          size="large"
          icon={
            isOpen ? (
              <CloseOutlined />
            ) : (
              <LeoIconImage
                src={buttonIconPath}
                alt="Leo AI"
                style={{ width: "32px", height: "32px" }}
              />
            )
          }
        />
      )}

      {/* Chat Widget: full-screen (desktop listing) or floating panel */}
      {isOpen && useFullScreenLayout ? (
        <FullScreenOverlay>
          <ChatHeaderFullWidth>
            <ChatHeaderInner>
              <Avatar
                src={iconPath}
                style={{
                  backgroundColor: "var(--color-primary-dark)",
                  marginRight: "12px",
                }}
                size={40}
              />
              <TitleContainer>
                <ChatTitle>{title}</ChatTitle>
                {subtitle ? <ChatSubtitle>{subtitle}</ChatSubtitle> : null}
              </TitleContainer>
              {messages.length > 0 && (
                <>
                  {!isMobile && (
                    <ResetButton
                      onClick={openResetConfirm}
                      title="Reset chat"
                      disabled={isResetDisabled}
                    >
                      <ReloadOutlined
                        style={{
                          filter: "brightness(0) invert(1)",
                          fontSize: "16px",
                        }}
                      />
                    </ResetButton>
                  )}
                  {isMobile && (
                    <MobileResetButtonInHeader
                      onClick={openResetConfirm}
                      title="Reset chat"
                      disabled={isResetDisabled}
                    >
                      <ReloadOutlined
                        style={{
                          filter: "brightness(0) invert(1)",
                          fontSize: "16px",
                        }}
                      />
                    </MobileResetButtonInHeader>
                  )}
                </>
              )}
              <MobileCloseButton onClick={toggleWidget}>
                <CloseOutlined style={{ filter: "brightness(0) invert(1)" }} />
              </MobileCloseButton>
            </ChatHeaderInner>
          </ChatHeaderFullWidth>
          <FullScreenColumn>
            {!isFullyOpen && (
              <LoadingScreenFullScreen
                className={isLoadingFading ? "fade-out" : ""}
              >
                <LogoContainer>
                  <LoadingLeoMark $src={iconPath} aria-label="Leo AI" />
                </LogoContainer>
              </LoadingScreenFullScreen>
            )}
            <ChatContentFullScreen
              className={isFullyOpen ? "visible" : "hidden"}
            >
              {isResetConfirmOpen && (
                <ResetConfirmOverlay
                  role="dialog"
                  aria-modal="true"
                  aria-label="Reset chat confirmation"
                  onClick={() => setIsResetConfirmOpen(false)}
                >
                  <ResetConfirmCard onClick={(e) => e.stopPropagation()}>
                    <ResetConfirmTitle>Reset chat?</ResetConfirmTitle>
                    <ResetConfirmText>
                      This will clear the conversation
                      <br />
                      and start a fresh chat.
                    </ResetConfirmText>
                    <ResetConfirmActions>
                      <ResetConfirmButton
                        type="button"
                        onClick={() => setIsResetConfirmOpen(false)}
                      >
                        Cancel
                      </ResetConfirmButton>
                      <ResetConfirmDangerButton
                        type="button"
                        onClick={() => {
                          setIsResetConfirmOpen(false);
                          handleResetChat();
                        }}
                      >
                        Yes, reset
                      </ResetConfirmDangerButton>
                    </ResetConfirmActions>
                  </ResetConfirmCard>
                </ResetConfirmOverlay>
              )}
              <ChatMessagesFullScreen>
                {isLoadingInitialMessage && (
                  <MessageItem $type="ai">
                    <MessageAvatar $type="ai">
                      <LeoMessageMark $src={iconPath} aria-label="Leo AI" />
                    </MessageAvatar>
                    <MessageContent $type="ai">
                      <TypingIndicator>
                        <TypingDot />
                        <TypingDot />
                        <TypingDot />
                      </TypingIndicator>
                    </MessageContent>
                  </MessageItem>
                )}
                {initialMessageError &&
                  !isLoadingInitialMessage &&
                  messages.length === 0 && (
                    <MessageItem $type="ai">
                      <MessageAvatar $type="ai">
                        <LeoMessageMark $src={iconPath} aria-label="Leo AI" />
                      </MessageAvatar>
                      <MessageContent $type="ai">
                        <MessageText>
                          Sorry, I couldn't load my initial message.{" "}
                          <RetryButton
                            onClick={() => {
                              setInitialMessageError(false);
                              setIsLoadingInitialMessage(true);
                              fetchInitialMessage();
                            }}
                          >
                            Try again
                          </RetryButton>
                        </MessageText>
                      </MessageContent>
                    </MessageItem>
                  )}
                {messages.map((msg) => (
                  <MessageItem key={msg.id} $type={msg.type}>
                    <MessageAvatar $type={msg.type}>
                      {msg.type === "ai" ? (
                        <LeoMessageMark $src={iconPath} aria-label="Leo AI" />
                      ) : (
                        <img
                          src="/icons/profile.svg"
                          alt="You"
                          aria-hidden="true"
                        />
                      )}
                    </MessageAvatar>
                    <MessageContent $type={msg.type}>
                      {msg.kind && renderCustomMessage
                        ? renderCustomMessage(msg, {
                            onAction: handleCustomAction,
                          })
                        : null}
                      {(!msg.kind || !renderCustomMessage) && (
                        <>
                          <MessageText $type={msg.type}>
                            {renderLeoMarkdown(msg.content)}
                          </MessageText>
                          {msg.timestamp && (
                            <MessageTime $type={msg.type}>
                              {msg.timestamp.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </MessageTime>
                          )}
                          {msg.decisionExplanation && (
                            <ViewLogicLink
                              type="button"
                              onClick={() =>
                                setViewLogicModal({
                                  open: true,
                                  text: msg.decisionExplanation,
                                })
                              }
                            >
                              View decision logic
                            </ViewLogicLink>
                          )}
                        </>
                      )}
                    </MessageContent>
                  </MessageItem>
                ))}
                {isTyping && (
                  <MessageItem $type="ai">
                    <MessageAvatar $type="ai">
                      <LeoMessageMark $src={iconPath} aria-label="Leo AI" />
                    </MessageAvatar>
                    <MessageContent $type="ai">
                      <TypingIndicator>
                        <TypingDot />
                        <TypingDot />
                        <TypingDot />
                      </TypingIndicator>
                    </MessageContent>
                  </MessageItem>
                )}
                <div ref={messagesEndRef} />
              </ChatMessagesFullScreen>
              <ChatInput>
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    isLoadingInitialMessage ? "Loading..." : placeholder
                  }
                  disabled={isLoadingInitialMessage}
                  suffix={
                    <Button
                      type="text"
                      icon={<SendOutlined />}
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || isLoadingInitialMessage}
                      style={{ color: "var(--color-primary)" }}
                    />
                  }
                />
              </ChatInput>
              {showFullDisclaimer ? (
                <Disclaimer>
                  <DisclaimerText>
                    {showMedicalDisclaimer &&
                      "Please do not share sensitive medical or health information. "}
                    Leo AI can make mistakes. Check important info.
                    <DisclaimerTermsBlock>
                      {" "}
                      By using this service, you agree to our{" "}
                      <DisclaimerLink
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Use
                      </DisclaimerLink>{" "}
                      and{" "}
                      <DisclaimerLink
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </DisclaimerLink>
                      .
                      {showAiDisclaimerFooter &&
                        " AI-generated response. Final decisions subject to human review."}
                    </DisclaimerTermsBlock>
                  </DisclaimerText>
                </Disclaimer>
              ) : (
                <Disclaimer>
                  <DisclaimerText>
                    {showMedicalDisclaimer &&
                      "Please do not share sensitive medical or health information. "}
                    Leo AI can make mistakes. Check important info.
                    {showAiDisclaimerFooter &&
                      " AI-generated response. Final decisions subject to human review."}
                  </DisclaimerText>
                </Disclaimer>
              )}
              {viewLogicModal.open && (
                <Modal
                  title="Decision logic"
                  open={viewLogicModal.open}
                  onCancel={() => setViewLogicModal({ open: false, text: "" })}
                  footer={[
                    <Button
                      key="close"
                      type="primary"
                      onClick={() =>
                        setViewLogicModal({ open: false, text: "" })
                      }
                    >
                      Close
                    </Button>,
                  ]}
                >
                  <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                    {viewLogicModal.text}
                  </p>
                </Modal>
              )}
            </ChatContentFullScreen>
          </FullScreenColumn>
        </FullScreenOverlay>
      ) : isOpen ? (
        <ChatWidget $isOpen={isOpen}>
          {/* Loading Screen with placeholder icon */}
          {!isFullyOpen && (
            <LoadingScreen className={isLoadingFading ? "fade-out" : ""}>
              <LogoContainer>
                <LoadingLeoMark $src={iconPath} aria-label="Leo AI" />
              </LogoContainer>
            </LoadingScreen>
          )}

          <ChatContent className={isFullyOpen ? "visible" : "hidden"}>
            <ChatHeader>
              <Avatar
                src={iconPath}
                style={{
                  backgroundColor: "var(--color-primary-dark)",
                  marginRight: "12px",
                }}
                size={40}
              />
              <TitleContainer>
                <ChatTitle>{title}</ChatTitle>
                {subtitle ? <ChatSubtitle>{subtitle}</ChatSubtitle> : null}
              </TitleContainer>
              {messages.length > 0 && (
                <>
                  {!isMobile && (
                    <ResetButton
                      onClick={openResetConfirm}
                      title="Reset chat"
                      disabled={isResetDisabled}
                    >
                      <ReloadOutlined
                        style={{
                          filter: "brightness(0) invert(1)",
                          fontSize: "16px",
                        }}
                      />
                    </ResetButton>
                  )}
                  {isMobile && (
                    <MobileResetButtonInHeader
                      onClick={openResetConfirm}
                      title="Reset chat"
                      disabled={isResetDisabled}
                    >
                      <ReloadOutlined
                        style={{
                          filter: "brightness(0) invert(1)",
                          fontSize: "16px",
                        }}
                      />
                    </MobileResetButtonInHeader>
                  )}
                </>
              )}
              <MobileCloseButton onClick={toggleWidget}>
                <CloseOutlined style={{ filter: "brightness(0) invert(1)" }} />
              </MobileCloseButton>
            </ChatHeader>

            {isResetConfirmOpen && (
              <ResetConfirmOverlay
                role="dialog"
                aria-modal="true"
                aria-label="Reset chat confirmation"
                onClick={() => setIsResetConfirmOpen(false)}
              >
                <ResetConfirmCard onClick={(e) => e.stopPropagation()}>
                  <ResetConfirmTitle>Reset chat?</ResetConfirmTitle>
                  <ResetConfirmText>
                    This will clear the conversation
                    <br />
                    and start a fresh chat.
                  </ResetConfirmText>
                  <ResetConfirmActions>
                    <ResetConfirmButton
                      type="button"
                      onClick={() => setIsResetConfirmOpen(false)}
                    >
                      Cancel
                    </ResetConfirmButton>
                    <ResetConfirmDangerButton
                      type="button"
                      onClick={() => {
                        setIsResetConfirmOpen(false);
                        handleResetChat();
                      }}
                    >
                      Yes, reset
                    </ResetConfirmDangerButton>
                  </ResetConfirmActions>
                </ResetConfirmCard>
              </ResetConfirmOverlay>
            )}

            <ChatMessages>
              {isLoadingInitialMessage && (
                <MessageItem $type="ai">
                  <MessageAvatar $type="ai">
                    <LeoMessageMark $src={iconPath} aria-label="Leo AI" />
                  </MessageAvatar>
                  <MessageContent $type="ai">
                    <TypingIndicator>
                      <TypingDot />
                      <TypingDot />
                      <TypingDot />
                    </TypingIndicator>
                  </MessageContent>
                </MessageItem>
              )}

              {initialMessageError &&
                !isLoadingInitialMessage &&
                messages.length === 0 && (
                  <MessageItem $type="ai">
                    <MessageAvatar $type="ai">
                      <LeoMessageMark $src={iconPath} aria-label="Leo AI" />
                    </MessageAvatar>
                    <MessageContent $type="ai">
                      <MessageText>
                        Sorry, I couldn't load my initial message.
                        <RetryButton
                          onClick={() => {
                            setInitialMessageError(false);
                            setIsLoadingInitialMessage(true);
                            fetchInitialMessage();
                          }}
                        >
                          Try again
                        </RetryButton>
                      </MessageText>
                    </MessageContent>
                  </MessageItem>
                )}

              {!isLoadingInitialMessage &&
                !initialMessageError &&
                messages.map((message) => (
                  <MessageItem key={message.id} $type={message.type}>
                    {message.type === "ai" && (
                      <MessageAvatar $type={message.type}>
                        <LeoMessageMark $src={iconPath} aria-label="Leo AI" />
                      </MessageAvatar>
                    )}
                    <MessageContent $type={message.type}>
                      {(() => {
                        const customNode = renderCustomMessage?.(message, {
                          onAction: handleCustomAction,
                        });
                        if (customNode) return customNode;
                        if (message.content && String(message.content).trim()) {
                          return (
                            <>
                              <MessageText $type={message.type}>
                                {renderLeoMarkdown(message.content)}
                              </MessageText>
                              {message.type === "ai" &&
                                message.decisionExplanation && (
                                  <ViewLogicLink
                                    onClick={() =>
                                      setViewLogicModal({
                                        open: true,
                                        text: message.decisionExplanation,
                                      })
                                    }
                                  >
                                    View Logic
                                  </ViewLogicLink>
                                )}
                            </>
                          );
                        }
                        return null;
                      })()}
                      <MessageTime $type={message.type}>
                        {message.timestamp.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </MessageTime>
                    </MessageContent>
                  </MessageItem>
                ))}

              {isTyping && (
                <MessageItem $type="ai">
                  <MessageAvatar $type="ai">
                    <LeoMessageMark $src={iconPath} aria-label="Leo AI" />
                  </MessageAvatar>
                  <MessageContent $type="ai">
                    <TypingIndicator>
                      <TypingDot />
                      <TypingDot />
                      <TypingDot />
                    </TypingIndicator>
                  </MessageContent>
                </MessageItem>
              )}

              <div ref={messagesEndRef} />
            </ChatMessages>

            <ChatInput>
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  isLoadingInitialMessage ? "Loading..." : placeholder
                }
                disabled={isLoadingInitialMessage}
                suffix={
                  <Button
                    type="text"
                    icon={<SendOutlined />}
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isLoadingInitialMessage}
                    style={{ color: "var(--color-primary)" }}
                  />
                }
              />
            </ChatInput>
            {showFullDisclaimer ? (
              <Disclaimer>
                <DisclaimerText>
                  {showMedicalDisclaimer &&
                    "Please do not share sensitive medical or health information. "}
                  Leo AI can make mistakes. Check important info.
                  <DisclaimerTermsBlock>
                    {" "}
                    By using this service, you agree to our{" "}
                    <DisclaimerLink
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Use
                    </DisclaimerLink>{" "}
                    and{" "}
                    <DisclaimerLink
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </DisclaimerLink>
                    .
                    {showAiDisclaimerFooter &&
                      " AI-generated response. Final decisions subject to human review."}
                  </DisclaimerTermsBlock>
                </DisclaimerText>
              </Disclaimer>
            ) : (
              <Disclaimer>
                <DisclaimerText>
                  {showMedicalDisclaimer &&
                    "Please do not share sensitive medical or health information. "}
                  Leo AI can make mistakes. Check important info.
                  {showAiDisclaimerFooter &&
                    " AI-generated response. Final decisions subject to human review."}
                </DisclaimerText>
              </Disclaimer>
            )}
            {viewLogicModal.open && (
              <Modal
                title="Decision logic"
                open={viewLogicModal.open}
                onCancel={() => setViewLogicModal({ open: false, text: "" })}
                footer={[
                  <Button
                    key="close"
                    type="primary"
                    onClick={() => setViewLogicModal({ open: false, text: "" })}
                  >
                    Close
                  </Button>,
                ]}
              >
                <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  {viewLogicModal.text}
                </p>
              </Modal>
            )}
          </ChatContent>
        </ChatWidget>
      ) : null}
    </WidgetContainer>
  );
};

export default LeoChatWidget;

// Styled Components (copied from AIAssistantWidget)
const WidgetContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 10000002 !important;
  pointer-events: auto;
  animation: slideInRight 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  @media ${device.mobile} {
    bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
    right: calc(1rem + env(safe-area-inset-right, 0px));
    left: auto;
    top: auto;
    width: auto;
    height: auto;
  }

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const FloatingButton = styled(Button)`
  width: 68px !important;
  height: 68px !important;
  min-width: 68px !important;
  min-height: 68px !important;
  max-width: 68px !important;
  max-height: 68px !important;
  border-radius: 50% !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: none !important;
  background: var(--gradient-primary) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  padding: 0 !important;
  position: relative;
  overflow: visible;

  /* Pulsing animation to draw attention - outward fade only */
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: var(--color-primary);
    opacity: 0;
    transform: translate(-50%, -50%) scale(1);
    animation: pulseOutward 2s ease-out infinite;
    z-index: -1;
    pointer-events: none;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.5) !important;
    background: var(--gradient-primary) !important;
    border-color: transparent !important;

    .anticon,
    .anticon svg,
    img {
      opacity: 1 !important;
      filter: none !important;
    }
  }

  &:focus {
    background: var(--gradient-primary) !important;
    border-color: transparent !important;
  }

  &:active {
    background: var(--gradient-primary) !important;
    border-color: transparent !important;
  }

  @keyframes pulseOutward {
    0% {
      opacity: 0.4;
      transform: translate(-50%, -50%) scale(1);
    }
    100% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(1.5);
    }
  }

  .anticon {
    font-size: 28px !important;
    color: white !important;
  }

  /* Make close icon (X) smaller when widget is open - 5% smaller */
  ${(props) =>
    props.$isOpen &&
    `
    .anticon {
      font-size: 23px !important;
    }
  `}

  /* Ensure close icon (X) is white when widget is open */
  .anticon svg {
    color: white !important;
    fill: white !important;
  }

  /* Style for img elements passed as icon */
  .ant-btn-icon {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    img {
      width: 32px !important;
      height: 32px !important;
      object-fit: contain;
      display: block;
    }
  }

  /* Direct img styling when not wrapped in .ant-btn-icon */
  img {
    width: 32px !important;
    height: 32px !important;
    object-fit: contain;
    display: block;
  }

  @media ${device.mobile} {
    width: 60px !important;
    height: 60px !important;
    min-width: 60px !important;
    min-height: 60px !important;
    max-width: 60px !important;
    max-height: 60px !important;

    .anticon {
      font-size: 24px !important;
    }

    /* Make close icon (X) smaller when widget is open on mobile - 5% smaller */
    ${(props) =>
      props.$isOpen &&
      `
      .anticon {
        font-size: 19px !important;
      }
    `}

    .ant-btn-icon img,
    img {
      width: 24px !important;
      height: 24px !important;
    }
  }
`;

const ChatWidget = styled.div`
  position: absolute;
  bottom: 80px;
  right: 0;
  width: 450px;
  height: 600px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transform-origin: bottom right;
  opacity: 1;
  animation: scaleOpen 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border: 1px solid rgba(0, 0, 0, 0.05);

  @keyframes scaleOpen {
    0% {
      transform: scale(0.1);
      border-radius: 50%;
    }
    100% {
      transform: scale(1);
      border-radius: 24px;
    }
  }

  @media ${device.mobile} {
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    height: 100dvh;
    max-height: 100dvh;
    min-height: -webkit-fill-available;
    padding-top: env(safe-area-inset-top, 0px);
    box-sizing: border-box;
    border-radius: 0;
    z-index: 10000003;
    box-shadow: none;
    border: none;
    -webkit-tap-highlight-color: transparent;
  }
`;

// Full-screen desktop layout (ChatGPT-style) for listing agent chat (no side borders)
const FullScreenOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000002;
  background: white;
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.2s ease-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const FullScreenColumn = styled.div`
  max-width: 48rem;
  width: 100%;
  flex: 1;
  min-height: 0;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background: white;
  box-shadow: none;
  position: relative;
  overflow: hidden;
`;

const LoadingScreenFullScreen = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: opacity 0.3s ease;
  &.fade-out {
    opacity: 0;
    pointer-events: none;
  }
`;

const ChatContentFullScreen = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  &.visible {
    opacity: 1;
  }
  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: 1.25rem 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  background: #fafafa;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 3px;
    &:hover {
      background: rgba(0, 0, 0, 0.25);
    }
  }
`;

const ChatMessagesFullScreen = styled.div`
  flex: 1;
  min-height: 0;
  padding: 1.25rem 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  background: #fafafa;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 3px;
    &:hover {
      background: rgba(0, 0, 0, 0.25);
    }
  }
`;

const ChatHeader = styled.div`
  background: var(--gradient-primary);
  color: white;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .ant-avatar {
    img {
      width: 60% !important;
      height: 60% !important;
      object-fit: contain;
    }
  }
`;

const ChatHeaderFullWidth = styled.div`
  width: 100%;
  background: var(--gradient-primary);
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ChatHeaderInner = styled.div`
  max-width: 48rem;
  margin: 0 auto;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  color: white;
  .ant-avatar {
    img {
      width: 60% !important;
      height: 60% !important;
      object-fit: contain;
    }
  }
`;

const TitleContainer = styled.div`
  flex: 1;
  text-align: left;
`;

const ChatTitle = styled.h3`
  margin: 0 0 0.125rem 0;
  font-family: var(--font-primary);
  font-weight: 700;
  font-size: 1.125rem;
  color: white;
  letter-spacing: -0.01em;
  text-align: left;
`;

const ChatSubtitle = styled.p`
  margin: 0;
  font-family: var(--font-secondary);
  font-size: 0.8125rem;
  opacity: 0.95;
  color: white;
  font-weight: 400;
  text-align: left;
`;

const MessageItem = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;

  ${(props) =>
    props.$type === "user" &&
    `
    flex-direction: row-reverse;
  `}
`;

const MessageAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${(props) =>
    props.$type === "ai" ? "white" : "var(--color-primary)"};
  color: ${(props) => (props.$type === "ai" ? "black" : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: ${(props) =>
    props.$type === "ai" ? "1px solid var(--color-border-light)" : "none"};
  ${(props) =>
    props.$type === "user" &&
    `
    img {
      width: 85%;
      height: 85%;
      object-fit: contain;
      filter: brightness(0) invert(1);
    }
  `}
`;

// Tint Leo avatar icon to primary brand green for AI messages
const LeoMessageMark = styled.div`
  width: 16px;
  height: 16px;
  background-color: var(--color-primary);
  mask: url(${(props) => props.$src}) no-repeat center / contain;
  -webkit-mask: url(${(props) => props.$src}) no-repeat center / contain;
`;

const MessageContent = styled.div`
  background: white;
  padding: 0.875rem 1.125rem;
  border-radius: 18px 18px 18px 4px;
  max-width: 75%;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.08),
    0 2px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);

  ${(props) =>
    props.$type === "user" &&
    `
    background: var(--color-primary);
    color: white;
    border-radius: 18px 18px 4px 18px;
    box-shadow: 0 2px 4px rgba(44, 95, 45, 0.2), 0 1px 2px rgba(44, 95, 45, 0.15);
    border: none;
  `}
`;

const MessageText = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.9375rem;
  line-height: 1.5;
  margin-bottom: 0.375rem;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: ${(props) => (props.$type === "user" ? "white" : "var(--color-text)")};
  text-align: left;

  strong {
    font-weight: 700;
    color: inherit;
  }
`;

const MessageTime = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.6875rem;
  color: ${(props) =>
    props.$type === "user"
      ? "rgba(255, 255, 255, 0.7)"
      : "var(--color-text-muted)"};
  margin-top: 0.125rem;
  font-weight: 400;
  text-align: left;
`;

const ViewLogicLink = styled.button`
  display: inline-block;
  margin-top: 0.375rem;
  padding: 0;
  border: none;
  background: none;
  font-family: var(--font-secondary);
  font-size: 0.8125rem;
  color: var(--color-primary);
  cursor: pointer;
  text-decoration: underline;
  &:hover {
    color: var(--color-primary-dark);
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 0.5rem 0;
`;

const TypingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-primary);
  animation: typing 1.4s infinite ease-in-out;

  &:nth-child(1) {
    animation-delay: 0s;
    opacity: var(--leo-typing-dot-opacity-1);
  }

  &:nth-child(2) {
    animation-delay: 0.2s;
    opacity: var(--leo-typing-dot-opacity-2);
  }

  &:nth-child(3) {
    animation-delay: 0.4s;
    opacity: var(--leo-typing-dot-opacity-3);
  }

  @keyframes typing {
    0%,
    60%,
    100% {
      transform: translateY(0);
      opacity: 0.7;
    }
    30% {
      transform: translateY(-10px);
      opacity: 1;
    }
  }
`;

const ChatInput = styled.div`
  padding: 0.75rem 1rem;
  padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--color-border-light);
  background: white;
  flex-shrink: 0;

  .ant-input-affix-wrapper {
    border: 1.5px solid var(--color-border-light);
    border-radius: 24px;
    padding: 0.75rem 1rem;
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--color-primary);
    }

    &:focus-within {
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1);
    }
  }

  .ant-input {
    border: none !important;
    font-family: var(--font-secondary);
    font-size: 0.9375rem;
    padding: 0;
    box-shadow: none !important;

    &::placeholder {
      color: var(--color-text-muted);
      opacity: 0.6;
    }

    &:focus {
      box-shadow: none !important;
      border: none !important;
    }
  }

  .ant-btn {
    border: none;
    box-shadow: none;

    &:hover {
      background: transparent;
      color: var(--color-primary);
    }
  }
`;

const Disclaimer = styled.div`
  margin: 0.5rem 1rem 1rem 1rem;
  text-align: center;
  padding: 0 0.5rem;

  @media ${device.mobile} {
    margin-bottom: 1.5rem;
  }
`;

const DisclaimerText = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-muted);
  line-height: 1.5;
  font-weight: 400;
  opacity: 0.75;
`;

const DisclaimerTermsBlock = styled.span`
  font-size: 0.75rem;
  color: var(--color-text-muted);
  opacity: 0.75;
  display: inline;
  @media (min-width: 769px) {
    display: block;
    margin-top: 0.5rem;
  }
`;

const DisclaimerLink = styled.a`
  color: var(--color-primary);
  text-decoration: underline;
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const ResetButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media ${device.mobile} {
    display: none;
  }
`;

const MobileResetButtonInHeader = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media ${device.mobile} {
    display: flex;
  }
`;

/* In-widget reset confirmation (covers the entire chat UI) */
const ResetConfirmOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000010;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  margin: 0;
  box-sizing: border-box;
  text-align: center !important;

  @media ${device.mobile} {
    padding: 1rem;
  }
`;

const ResetConfirmCard = styled.div`
  width: 100%;
  max-width: 360px;
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow:
    0 12px 48px rgba(0, 0, 0, 0.15),
    0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 1.5rem 1.5rem 1.25rem 1.5rem;
  margin: 0 auto;
  flex-shrink: 0;

  @media ${device.mobile} {
    border-radius: 16px;
    padding: 1.25rem 1.25rem 1rem 1.25rem;
  }
`;

const ResetConfirmTitle = styled.div`
  font-family: var(--font-primary);
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.01em;
  font-size: 1.125rem;
  margin-bottom: 0.5rem;

  @media ${device.mobile} {
    font-size: 1.05rem;
    margin-bottom: 0.4rem;
  }
`;

const ResetConfirmText = styled.div`
  font-family: var(--font-secondary);
  color: var(--color-text-light);
  line-height: 1.5;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
`;

const ResetConfirmActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1.25rem;

  @media ${device.mobile} {
    gap: 0.65rem;
    margin-top: 1rem;
  }
`;

const ResetConfirmButton = styled.button`
  height: 44px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  background: #ffffff;
  color: var(--color-text);
  font-weight: 600;
  font-family: var(--font-secondary);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-bg-secondary);
    border-color: var(--color-border-light);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ResetConfirmDangerButton = styled.button`
  height: 44px;
  border-radius: 12px;
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: white;
  font-weight: 600;
  font-family: var(--font-secondary);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.2);

  &:hover {
    background: var(--color-primary-dark);
    border-color: var(--color-primary-dark);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const MobileCloseButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media ${device.mobile} {
    display: flex;
  }
`;

const WelcomeMessage = styled.div`
  position: absolute;
  bottom: 82px;
  right: 6px;
  background: white;
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1.25rem;
  box-shadow:
    0 8px 32px rgba(44, 95, 45, 0.15),
    0 4px 16px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  width: fit-content;
  min-width: 250px;
  z-index: 10000001;
  animation: slideInUp 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition: all 0.2s ease;

  &:hover {
    box-shadow:
      0 12px 40px rgba(44, 95, 45, 0.2),
      0 6px 20px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  @keyframes slideInUp {
    from {
      transform: translateY(10px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media ${device.mobile} {
    bottom: 74px;
    right: 0.25rem;
    left: auto;
    max-width: calc(100vw - 5rem);
    width: auto;
    padding: 0.75rem 1.25rem;
  }
`;

const Arrow = styled.div`
  position: absolute;
  bottom: -8px;
  right: 20px;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid white;
`;

const RetryButton = styled.button`
  background: none;
  border: none;
  color: var(--color-primary);
  font-family: var(--font-secondary);
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  margin-left: 0.5rem;
  padding: 0;

  &:hover {
    color: var(--color-primary-dark);
  }
`;

const ChatContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;

  &.visible {
    opacity: 1;
  }

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`;

const LoadingScreen = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: opacity 0.3s ease;

  &.fade-out {
    opacity: 0;
    pointer-events: none;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: logoPulse 2s ease-in-out infinite;

  @keyframes logoPulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`;

const LeoIconImage = styled.img`
  object-fit: contain;
  user-select: none;
  pointer-events: none;
`;

// Used ONLY in the open-transition loading screen to tint the PNG to brand green.
// This uses a CSS mask so the icon becomes a solid `var(--color-primary)` silhouette.
const LoadingLeoMark = styled.div`
  width: 120px;
  height: 120px;
  background: var(--color-primary);
  -webkit-mask-image: url(${(p) => p.$src});
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
  mask-image: url(${(p) => p.$src});
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  user-select: none;
  pointer-events: none;
`;
